import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { userSettingsApi, adminApi, pushApi } from '../api';
import DashboardLayout from '../components/dashboard/DashboardLayout';
import { Settings, Mail, Bell, Save, Loader2, CheckCircle, KeyRound, Smartphone } from 'lucide-react';

const STRIPE_FIELDS = [
  { key: 'stripe_publishable_key', label: 'Publishable key', secret: false },
  { key: 'stripe_secret_key', label: 'Secret key', secret: true },
  { key: 'stripe_webhook_secret', label: 'Webhook signing secret', secret: true },
];

// base64url -> Uint8Array (applicationServerKey dla Push API)
function urlB64ToUint8Array(base64) {
  const padding = '='.repeat((4 - (base64.length % 4)) % 4);
  const b64 = (base64 + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = atob(b64);
  return Uint8Array.from([...raw].map((c) => c.charCodeAt(0)));
}

export default function SettingsPage() {
  const { user } = useAuth();
  const [emailEnabled, setEmailEnabled] = useState(user?.email_notifications_enabled !== false);
  const [digestEnabled, setDigestEnabled] = useState(user?.weekly_digest_enabled !== false);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  // Web push
  const [pushOn, setPushOn] = useState(null);  // null = ładowanie
  const [pushBusy, setPushBusy] = useState(false);

  // Config-in-UI (admin only)
  const [adminCfg, setAdminCfg] = useState(null);
  const [cfgForm, setCfgForm] = useState({
    stripe_publishable_key: '', stripe_secret_key: '', stripe_webhook_secret: '',
  });
  const [plans, setPlans] = useState([]);
  const [priceForm, setPriceForm] = useState({});
  const [savingCfg, setSavingCfg] = useState(false);
  const [savedCfg, setSavedCfg] = useState(false);

  useEffect(() => {
    adminApi.getConfig()
      .then(r => setAdminCfg(r.data?.config || {}))
      .catch(() => setAdminCfg(null));
    adminApi.getPlans()
      .then(r => {
        const ps = r.data?.items || [];
        setPlans(ps);
        const pf = {};
        ps.forEach(p => { pf[p.id] = p.stripe_price_id || ''; });
        setPriceForm(pf);
      })
      .catch(() => {});
    // stan subskrypcji push
    if (!('serviceWorker' in navigator) || !('PushManager' in window)) {
      setPushOn(false);
    } else {
      navigator.serviceWorker.ready
        .then(reg => reg.pushManager.getSubscription())
        .then(sub => setPushOn(!!sub))
        .catch(() => setPushOn(false));
    }
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      await userSettingsApi.update({
        email_notifications_enabled: emailEnabled,
        weekly_digest_enabled: digestEnabled,
      });
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
    } catch (e) {
      alert('Błąd zapisu: ' + (e?.response?.data?.detail || e.message));
    } finally { setSaving(false); }
  };

  const togglePush = async () => {
    setPushBusy(true);
    try {
      const reg = await navigator.serviceWorker.ready;
      if (pushOn) {
        const sub = await reg.pushManager.getSubscription();
        if (sub) {
          await pushApi.unsubscribe(sub.endpoint).catch(() => {});
          await sub.unsubscribe();
        }
        setPushOn(false);
      } else {
        const perm = await Notification.requestPermission();
        if (perm !== 'granted') {
          alert('Brak zgody na powiadomienia w przeglądarce.');
          return;
        }
        const { data } = await pushApi.vapidKey();
        if (!data?.public_key) {
          alert('Web push niedostępny (brak klucza VAPID).');
          return;
        }
        const sub = await reg.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: urlB64ToUint8Array(data.public_key),
        });
        await pushApi.subscribe(sub);
        setPushOn(true);
      }
    } catch (e) {
      alert('Push: ' + (e?.response?.data?.detail || e.message));
    } finally { setPushBusy(false); }
  };

  const saveCfg = async () => {
    setSavingCfg(true);
    try {
      const payload = {};
      Object.keys(cfgForm).forEach(k => { if (cfgForm[k].trim()) payload[k] = cfgForm[k].trim(); });
      if (Object.keys(payload).length > 0) await adminApi.updateConfig(payload);
      for (const p of plans) {
        if (p.price_cents > 0 && (priceForm[p.id] || '') !== (p.stripe_price_id || '')) {
          await adminApi.updatePlan(p.id, { stripe_price_id: priceForm[p.id] || '' });
        }
      }
      const [rc, rp] = await Promise.all([adminApi.getConfig(), adminApi.getPlans()]);
      setAdminCfg(rc.data?.config || {});
      setPlans(rp.data?.items || []);
      setCfgForm({ stripe_publishable_key: '', stripe_secret_key: '', stripe_webhook_secret: '' });
      setSavedCfg(true);
      setTimeout(() => setSavedCfg(false), 2500);
    } catch (e) {
      alert('Błąd zapisu: ' + (e?.response?.data?.detail || e.message));
    } finally { setSavingCfg(false); }
  };

  return (
    <DashboardLayout>
      <h1 className="text-2xl font-bold text-slate-900 mb-6 flex items-center gap-2">
        <Settings className="w-6 h-6 text-slate-500" /> Ustawienia
      </h1>

      {/* Powiadomienia */}
      <div className="bg-white border border-slate-200 rounded-xl p-6 max-w-2xl">
        <h2 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
          <Bell className="w-5 h-5 text-indigo-500" /> Powiadomienia
        </h2>

        <div className="space-y-3">
          <label className="flex items-center justify-between gap-4 p-3 rounded hover:bg-slate-50">
            <div>
              <div className="font-medium text-slate-900 text-sm flex items-center gap-1">
                <Mail className="w-4 h-4" /> Email — natychmiastowe
              </div>
              <div className="text-xs text-slate-500 mt-0.5">
                Dostajesz email gdy ktoś skomentuje Twój post, polubi, AI moderator odpowie, otrzymasz nową wiadomość/zaproszenie.
              </div>
            </div>
            <input
              type="checkbox"
              checked={emailEnabled}
              onChange={e => setEmailEnabled(e.target.checked)}
              className="w-5 h-5"
            />
          </label>

          <label className="flex items-center justify-between gap-4 p-3 rounded hover:bg-slate-50">
            <div>
              <div className="font-medium text-slate-900 text-sm flex items-center gap-1">
                <Mail className="w-4 h-4" /> Weekly digest
              </div>
              <div className="text-xs text-slate-500 mt-0.5">
                Co poniedziałek email z podsumowaniem aktywności w Twoich społecznościach.
              </div>
            </div>
            <input
              type="checkbox"
              checked={digestEnabled}
              onChange={e => setDigestEnabled(e.target.checked)}
              className="w-5 h-5"
            />
          </label>

          <div className="flex items-center justify-between gap-4 p-3 rounded">
            <div>
              <div className="font-medium text-slate-900 text-sm flex items-center gap-1">
                <Smartphone className="w-4 h-4" /> Powiadomienia push
              </div>
              <div className="text-xs text-slate-500 mt-0.5">
                Push w przeglądarce i zainstalowanej aplikacji (PWA) — nowe wiadomości, komentarze, zaproszenia.
              </div>
            </div>
            <button
              onClick={togglePush}
              disabled={pushBusy || pushOn === null}
              className={`shrink-0 px-3 py-1.5 text-sm rounded disabled:opacity-50 ${pushOn ? 'border border-slate-300 text-slate-700 hover:bg-slate-50' : 'bg-indigo-600 text-white hover:bg-indigo-700'}`}
            >
              {pushBusy ? '…' : pushOn === null ? '…' : pushOn ? 'Wyłącz' : 'Włącz'}
            </button>
          </div>
        </div>

        <div className="flex items-center gap-3 mt-6 pt-4 border-t border-slate-100">
          <button
            onClick={save}
            disabled={saving}
            className="flex items-center gap-1 px-4 py-2 bg-indigo-600 text-white text-sm rounded hover:bg-indigo-700 disabled:opacity-50"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Zapisz
          </button>
          {saved && <span className="text-sm text-emerald-600 flex items-center gap-1"><CheckCircle className="w-4 h-4" /> Zapisano</span>}
        </div>
      </div>

      {/* Integracje — Stripe (tylko admin) */}
      {adminCfg && (
        <div className="bg-white border border-slate-200 rounded-xl p-6 max-w-2xl mt-6">
          <h2 className="text-lg font-semibold text-slate-900 mb-1 flex items-center gap-2">
            <KeyRound className="w-5 h-5 text-indigo-500" /> Integracje — Stripe
          </h2>
          <p className="text-xs text-slate-500 mb-4">
            Klucze API Stripe (zapis w bazie). Tryb test (<code>sk_test_…</code>) do developmentu, live przy produkcji.
          </p>

          <div className="space-y-3">
            {STRIPE_FIELDS.map(f => (
              <div key={f.key}>
                <label className="block text-xs font-medium text-slate-600 mb-1">
                  {f.label}{' '}
                  {adminCfg[f.key]?.set
                    ? <span className="text-emerald-600">— skonfigurowany ({adminCfg[f.key].value})</span>
                    : <span className="text-amber-600">— brak</span>}
                </label>
                <input
                  type={f.secret ? 'password' : 'text'}
                  value={cfgForm[f.key]}
                  onChange={e => setCfgForm(p => ({ ...p, [f.key]: e.target.value }))}
                  placeholder={adminCfg[f.key]?.set ? 'zostaw puste aby nie zmieniać' : 'wklej klucz'}
                  className="w-full px-3 py-2 border border-slate-200 rounded text-sm font-mono"
                />
              </div>
            ))}
          </div>

          <div className="mt-5 pt-4 border-t border-slate-100">
            <h3 className="text-sm font-semibold text-slate-900 mb-1">Stripe Price ID planów</h3>
            <p className="text-xs text-slate-500 mb-3">
              ID ceny ze Stripe Dashboard (np. <code>price_1Abc…</code>). Bez tego checkout danego planu zwróci błąd.
            </p>
            <div className="space-y-2">
              {plans.filter(p => p.price_cents > 0).map(p => (
                <div key={p.id} className="flex items-center gap-2">
                  <span className="text-sm text-slate-700 w-32 shrink-0">
                    {p.name} <span className="text-slate-400">${Math.round(p.price_cents / 100)}</span>
                  </span>
                  <input
                    value={priceForm[p.id] ?? ''}
                    onChange={e => setPriceForm(f => ({ ...f, [p.id]: e.target.value }))}
                    placeholder="price_…"
                    className="flex-1 px-3 py-1.5 border border-slate-200 rounded text-sm font-mono"
                  />
                </div>
              ))}
            </div>
          </div>

          <div className="flex items-center gap-3 mt-6 pt-4 border-t border-slate-100">
            <button
              onClick={saveCfg}
              disabled={savingCfg}
              className="flex items-center gap-1 px-4 py-2 bg-indigo-600 text-white text-sm rounded hover:bg-indigo-700 disabled:opacity-50"
            >
              {savingCfg ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Zapisz konfigurację
            </button>
            {savedCfg && <span className="text-sm text-emerald-600 flex items-center gap-1"><CheckCircle className="w-4 h-4" /> Zapisano</span>}
          </div>
        </div>
      )}
    </DashboardLayout>
  );
}
